import React from 'react';
import { Box, Container, Typography, Grid, Card, CardContent, CardMedia, Chip, useTheme, alpha, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import SEO, { createBreadcrumbSchema } from '../../components/SEO';

// Blog posts data - Rich content for SEO
const blogPosts = [
    {
        id: 'working-with-json-complete-guide',
        title: 'The Complete Guide to Working with JSON in 2026',
        excerpt: 'Master JSON parsing, validation, and transformation. Learn best practices for handling JSON data in modern web applications.',
        image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=600',
        category: 'JSON',
        author: 'UmbrellaCo Team',
        date: 'February 5, 2026',
        readTime: '8 min read',
        featured: true,
    },
    {
        id: 'csv-to-json-conversion-tips',
        title: 'CSV to JSON Conversion: Tips, Tricks & Best Practices',
        excerpt: 'Learn how to efficiently convert CSV files to JSON format. Handle edge cases, nested structures, and large datasets.',
        image: 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=600',
        category: 'Data Conversion',
        author: 'UmbrellaCo Team',
        date: 'February 3, 2026',
        readTime: '6 min read',
        featured: false,
    },
    {
        id: 'json-schema-validation-explained',
        title: 'JSON Schema Validation Explained: A Developer\'s Guide',
        excerpt: 'Understand JSON Schema from basics to advanced. Validate API responses, form data, and configuration files with confidence.',
        image: 'https://images.unsplash.com/photo-1516116216624-53e697fedbea?w=600',
        category: 'JSON',
        author: 'UmbrellaCo Team',
        date: 'January 28, 2026',
        readTime: '10 min read',
        featured: true,
    },
    {
        id: 'api-testing-best-practices',
        title: 'API Testing Best Practices for Modern Developers',
        excerpt: 'Learn effective API testing strategies. From unit tests to integration tests, build reliable APIs with confidence.',
        image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600',
        category: 'API',
        author: 'UmbrellaCo Team',
        date: 'January 25, 2026',
        readTime: '7 min read',
        featured: false,
    },
    {
        id: 'geojson-mapping-fundamentals',
        title: 'GeoJSON and Interactive Mapping Fundamentals',
        excerpt: 'Create stunning interactive maps with GeoJSON. From basic markers to complex polygons and data visualization.',
        image: 'https://images.unsplash.com/photo-1524661135-423995f22d0b?w=600',
        category: 'Spatial',
        author: 'UmbrellaCo Team',
        date: 'January 20, 2026',
        readTime: '9 min read',
        featured: false,
    },
    {
        id: 'text-encoding-base64-guide',
        title: 'Text Encoding Demystified: Base64, URL Encoding & More',
        excerpt: 'Understand text encoding fundamentals. When to use Base64, URL encoding, and how to handle special characters.',
        image: 'https://images.unsplash.com/photo-1504639725590-34d0984388bd?w=600',
        category: 'Text Tools',
        author: 'UmbrellaCo Team',
        date: 'January 15, 2026',
        readTime: '5 min read',
        featured: false,
    },
];

const categories = ['All', 'JSON', 'Data Conversion', 'API', 'Spatial', 'Text Tools'];

// SEO Schema
const breadcrumbSchema = createBreadcrumbSchema([
    { name: 'Home', url: 'https://umbrellaco.in' },
    { name: 'Blog', url: 'https://umbrellaco.in/blog' }
]);

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.1 }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
};

function BlogApp() {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const [activeCategory, setActiveCategory] = React.useState('All');
    const [searchQuery, setSearchQuery] = React.useState('');

    const filteredPosts = blogPosts.filter(post => {
        const matchesCategory = activeCategory === 'All' || post.category === activeCategory;
        const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    const featuredPost = blogPosts.find(post => post.featured);

    return (
        <Box sx={{ minHeight: '125vh', bgcolor: 'background.default' }}>
            <SEO
                title="Developer Blog - Tutorials, Tips & Best Practices | UmbrellaCo"
                description="Learn about JSON, APIs, data conversion, and developer tools. Tutorials, best practices, and tips for modern web development."
                keywords="developer blog, json tutorial, api best practices, data conversion, web development tips, programming tutorials"
                url="/blog"
                jsonLd={breadcrumbSchema}
            />

            {/* Hero Section */}
            <Box
                sx={{
                    background: isDark
                        ? 'radial-gradient(circle at 50% 0%, #0f172a 0%, #020617 100%)'
                        : `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                    py: { xs: 4, md: 6 },
                    color: 'white',
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
            >
                <Container maxWidth="lg">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <Typography variant="h2" sx={{ fontWeight: 900, mb: 1, fontSize: { xs: '2rem', md: '3.5rem' } }}>
                            Developer Blog
                        </Typography>
                        <Typography variant="h6" sx={{ opacity: 0.8, fontWeight: 400 }}>
                            Tutorials, tips, and best practices for modern developers
                        </Typography>
                    </motion.div>
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ py: 4 }}>
                {/* Filters and Search */}
                <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, justifyContent: 'space-between', alignItems: { md: 'center' } }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {categories.map(category => (
                            <Chip
                                key={category}
                                label={category}
                                onClick={() => setActiveCategory(category)}
                                variant={activeCategory === category ? 'filled' : 'outlined'}
                                color={activeCategory === category ? 'primary' : 'default'}
                                sx={{
                                    fontWeight: 500,
                                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) }
                                }}
                            />
                        ))}
                    </Box>
                    <TextField
                        placeholder="Search articles..."
                        size="small"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
                        }}
                        sx={{
                            minWidth: { xs: '100%', md: 300 },
                            '& .MuiOutlinedInput-root': { borderRadius: 2 }
                        }}
                    />
                </Box>

                {/* Featured Post */}
                {activeCategory === 'All' && featuredPost && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <Link to={`/blog/${featuredPost.id}`} style={{ textDecoration: 'none' }}>
                            <Card
                                sx={{
                                    mb: 4,
                                    display: 'flex',
                                    flexDirection: { xs: 'column', md: 'row' },
                                    overflow: 'hidden',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
                                    }
                                }}
                            >
                                <CardMedia
                                    component="img"
                                    sx={{
                                        width: { xs: '100%', md: 400 },
                                        height: { xs: 200, md: 'auto' },
                                        objectFit: 'cover'
                                    }}
                                    image={featuredPost.image}
                                    alt={featuredPost.title}
                                />
                                <CardContent sx={{ flex: 1, p: 4, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                    <Box sx={{ mb: 2 }}>
                                        <Chip
                                            label="Featured"
                                            size="small"
                                            color="primary"
                                            sx={{ fontWeight: 700 }}
                                        />
                                    </Box>
                                    <Typography variant="h4" sx={{ fontWeight: 900, mb: 2, color: 'text.primary', lineHeight: 1.2 }}>
                                        {featuredPost.title}
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.7 }}>
                                        {featuredPost.excerpt}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 3, color: 'text.secondary', fontSize: '0.85rem', fontWeight: 600 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <PersonIcon fontSize="small" color="primary" />
                                            {featuredPost.author}
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <AccessTimeIcon fontSize="small" color="primary" />
                                            {featuredPost.readTime}
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Link>
                    </motion.div>
                )}

                {/* Blog Grid */}
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                >
                    <Grid container spacing={3}>
                        {filteredPosts.filter(p => activeCategory !== 'All' || !p.featured).map((post) => (
                            <Grid item xs={12} sm={6} md={4} key={post.id}>
                                <motion.div variants={itemVariants}>
                                    <Link to={`/blog/${post.id}`} style={{ textDecoration: 'none' }}>
                                        <Card
                                            sx={{
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    transform: 'translateY(-8px)',
                                                    boxShadow: `0 15px 30px ${alpha(theme.palette.primary.main, 0.12)}`,
                                                }
                                            }}
                                        >
                                            <CardMedia
                                                component="img"
                                                height="180"
                                                image={post.image}
                                                alt={post.title}
                                                sx={{ objectFit: 'cover' }}
                                            />
                                            <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                                                <Chip
                                                    label={post.category}
                                                    size="small"
                                                    sx={{ mb: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', fontWeight: 700 }}
                                                />
                                                <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, color: 'text.primary', lineHeight: 1.3 }}>
                                                    {post.title}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, lineHeight: 1.6 }}>
                                                    {post.excerpt.substring(0, 120)}...
                                                </Typography>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', color: 'text.secondary', fontSize: '0.75rem', fontWeight: 600, mt: 'auto', pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                                                    <span>{post.date}</span>
                                                    <span>{post.readTime}</span>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Link>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </motion.div>
            </Container>
        </Box>
    );
}

export default BlogApp;
